<template>
    <div class="d-flex flex-column">
        <qrcode-stream
            :class="{ 'border-danger': valid === false }"
            :paused="paused"
            :track="paintBoundingBox"
            class="qrcode-stream-wrapper"
            @detect="onDetect"
            @error="onError"
            @camera-on="onReady"
        >
            <div class="d-flex justify-content-center align-items-center h-100">
                <div class="d-flex justify-content-center align-items-center w-100" style="position: absolute">
                    <div>
                        <LogoCurieCC :class="{ translucent: !isLoading }" :height="100" :width="100" />
                    </div>
                </div>

                <div v-if="isLoading" style="position: absolute">
                    <spinner class="m-2" color="primary" style="width: 1.5rem; height: 1.5rem" />
                </div>

                <div v-if="cameraError" class="text-center text-danger" style="position: absolute">
                    <div v-if="cameraError.name === 'NotAllowedError'" v-html="trans('general.camera.error.not_allowed')" />
                    <div v-else-if="cameraError.name === 'StreamLoadTimeoutError'" v-html="trans('general.camera.error.timeout')" />
                    <div v-else-if="cameraError.name === 'NotReadableError'" v-html="trans('general.camera.error.not_readable')" />
                    <div v-else v-html="trans('general.error.generic')" />
                </div>

                <div v-if="isLoading" class="d-flex justify-content-center align-items-center loading-label w-100">
                    <div>
                        {{ trans('qr_code_scanner.loading.label') }}
                    </div>
                </div>
                <div v-if="validationSuccess" class="validation-success d-flex justify-content-center align-items-center">
                    <icon class="text-success" icon="cil-check-circle" style="width: 5rem; height: 5rem" />
                </div>

                <div v-if="validationFailure" class="validation-failure d-flex justify-content-center align-items-center">
                    <icon class="text-danger" icon="cil-ban" style="width: 5rem; height: 5rem" />
                </div>
                <div v-if="validationPending" class="validation-success d-flex justify-content-center align-items-center">
                    <spinner class="m-2" style="width: 1.5rem; height: 1.5rem" />
                </div>
            </div>
        </qrcode-stream>
    </div>
</template>
<script>
import { QrcodeStream } from 'vue-qrcode-reader';
import LogoCurieCC from '../../icons/LogoCurieCC.vue';
import * as Sentry from '@sentry/vue';

// Camera/stream failures that are environmental (user/device/browser), never application bugs.
// These are surfaced to the user via cameraError and intentionally not reported to Sentry.
const ENVIRONMENTAL_CAMERA_ERRORS = [
    'NotAllowedError',
    'NotReadableError',
    'NotFoundError',
    'OverconstrainedError',
    'SecurityError',
    'StreamApiNotSupportedError',
    'InsecureContextError',
    'StreamLoadTimeoutError',
];

export default {
    props: ['valid'],

    emits: ['success', 'failure', 'reset'],

    components: {
        LogoCurieCC,
        QrcodeStream,
    },

    data() {
        return {
            result: null,
            isLoading: true,
            cameraError: null,
            paused: false,
        };
    },

    computed: {
        validationPending() {
            return this.valid === undefined && this.paused;
        },

        validationSuccess() {
            return this.valid === true;
        },

        validationFailure() {
            return this.valid === false;
        },
        hasBrowserCompatibility() {
            return !!navigator.mediaDevices;
        },
    },

    methods: {
        onReady(promise) {
            this.isLoading = false;
            this.resetValidationState();
        },
        resetValidationState() {
            this.$emit('reset');
            this.paused = false;
        },
        onDetect([firstDetectedCode]) {
            try {
                const code = this.retrieveCurieId(firstDetectedCode.rawValue);

                if (!code) {
                    return;
                }

                // Turn camera off
                this.paused = true;

                // Close modal
                this.$emit('success', code);
            } catch (error) {
                // Display errors
                this.qrCodeError = this.trans('qr_code_scanner.modal.error.message');

                this.$emit('failure', error);
            }
        },
        // curie ID v1
        parseJsonWithUuid(code) {
            try {
                let codeContainer = JSON.parse(code);

                if (typeof codeContainer === 'string') {
                    codeContainer = JSON.parse(codeContainer);
                }

                if (codeContainer.uuid) {
                    return codeContainer.uuid;
                }
            } catch {
                return null;
            }
            return null;
        },

        parseJsonWithQuotes(code) {
            try {
                let wrappedCode = code;
                if (!wrappedCode.startsWith('"')) {
                    wrappedCode = '"' + wrappedCode;
                }
                if (!wrappedCode.endsWith('"')) {
                    wrappedCode += '"';
                }
                return this.parseJsonWithUuid(wrappedCode);
            } catch {
                return null;
            }
        },

        // curie ID v2
        parseUrl(code) {
            try {
                // Try to parse as URL first
                const url = new URL(code);

                // Check if it's a curie.network URL
                if (!url.hostname.endsWith('curie.network')) {
                    return null;
                }

                // Extract the hex ID from the pathname
                const hexId = url.pathname.split('/').pop();

                // Validate that it's an 8-character hex string
                const hexPattern = /^[a-fA-F0-9]{8}$/;
                if (hexPattern.test(hexId)) {
                    return hexId;
                }
            } catch {
                // If it's not a URL, try direct hex string match
                const hexPattern = /^[a-fA-F0-9]{8}$/;
                if (hexPattern.test(code)) {
                    return code;
                }
            }
            return null;
        },

        // Returns the Curie ID encoded in the QR payload, or null when the code is not a Curie
        // code. Non-throwing, so it is safe to call from the per-frame paintBoundingBox painter
        // (which the library invokes inside an async scan loop, where a throw becomes an
        // unhandled rejection).
        findCurieId(code) {
            if (!code) {
                return null;
            }

            // Define the parsing strategies in order
            const strategies = [this.parseJsonWithUuid, this.parseJsonWithQuotes, this.parseUrl];

            // Try each strategy in sequence
            for (const strategy of strategies) {
                const result = strategy(code);
                if (result) {
                    return result;
                }
            }

            return null;
        },
        retrieveCurieId(code) {
            const result = this.findCurieId(code);

            if (!result) {
                throw new Error('qr_code_not_found');
            }

            return result;
        },
        onError(error) {
            this.isLoading = false;
            this.cameraError = error;

            // Known environmental failures are surfaced to the user via cameraError and not
            // reported. Anything else is unexpected (a new error type, a lib bug, a real defect
            // surfacing through the stream) -- still show the generic message, but report it so we
            // don't go blind. Never re-throw: qrcode-stream calls onError from an async scan loop,
            // where a throw becomes an unhandled rejection (the original Sentry issue).
            if (!ENVIRONMENTAL_CAMERA_ERRORS.includes(error?.name)) {
                Sentry.captureException(error);
            }
        },
        paintBoundingBox(detectedCodes, ctx) {
            for (const detectedCode of detectedCodes) {
                const { x, y, width, height } = detectedCode.boundingBox;
                let strokeColor = this.findCurieId(detectedCode.rawValue) ? '#00A37B' : '#CC0030';

                ctx.lineWidth = 4;
                ctx.strokeStyle = strokeColor;
                ctx.strokeRect(x, y, width, height);
            }
        },
    },
};
</script>
<style scoped>
.validation-success,
.validation-failure,
.validation-pending {
    position: absolute;
    width: 100%;
    height: 100%;

    background-color: rgba(255, 255, 255, 0.8);
    text-align: center;
    flex-flow: column nowrap;
}

.logo {
    transition: opacity 1s ease;
}

.translucent {
    opacity: 0;
}

.loading-label {
    margin-top: 150px;
}
</style>
